import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Button,
    IconButton,
    InputAdornment,
    LinearProgress,
    MenuItem,
    TextField,
} from '@material-ui/core';
import type { TextFieldProps } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { AssignmentInd, Close, RotateLeft, Search } from '@material-ui/icons';
import { KeyboardDatePicker } from '@material-ui/pickers';
import classNames from 'classnames';
import { isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Typography } from '../Typography';
import { filterPanelTheme, tableInCardTheme } from './filterPanelThemes';
import classes from './FilterPanel.module.scss';

type FilterCardProps = {
    // Content of the filter panel, usually a FilterStack or a FilterRow
    filters: React.ReactNode;
    // The table, shown in the same card below the filters
    children: React.ReactNode;
    // Shows a progress line above the table and dims it while the list loads
    loading?: boolean;
};

/**
 * Bordered card that groups the filters and the table of a list view, and
 * gives the fields inside one compact look.
 */
export function FilterCard({
    filters,
    children,
    loading = false,
}: FilterCardProps) {
    const { t } = useTranslation();

    return (
        <div className={classes.card}>
            <ThemeProvider theme={filterPanelTheme}>
                <div className={classes.panel}>{filters}</div>
            </ThemeProvider>
            <div
                className={classNames(classes.innhold, {
                    [classes.laster]: loading,
                })}
                aria-busy={loading}
            >
                {loading && (
                    <LinearProgress
                        className={classes.lastelinje}
                        color="secondary"
                        aria-label={t('filterPanel.laster')}
                    />
                )}
                <ThemeProvider theme={tableInCardTheme}>
                    {children}
                </ThemeProvider>
            </div>
        </div>
    );
}

type FilterRowProps = {
    onReset: () => void;
    children: React.ReactNode;
};

/** Row of filter fields with a «Nullstill» button at the end. */
export const FilterRow = ({ onReset, children }: FilterRowProps) => {
    const { t } = useTranslation();

    return (
        <div className={classes.row}>
            {children}
            <Button
                className={classes.resetButton}
                startIcon={<RotateLeft />}
                onClick={onReset}
            >
                {t('filterPanel.nullstill')}
            </Button>
        </div>
    );
};

type FilterSelectOption<T extends string | number> = {
    value: T;
    label: string;
};

type FilterSelectProps<T extends string | number> = {
    id: string;
    label: string;
    value: T;
    options: Array<FilterSelectOption<T>>;
    onChange: (value: T) => void;
    disabled?: boolean;
};

/** Single-choice dropdown for a FilterRow. */
export function FilterSelect<T extends string | number>({
    id,
    label,
    value,
    options,
    onChange,
    disabled,
}: FilterSelectProps<T>) {
    return (
        <TextField
            id={id}
            className={classes.select}
            select={true}
            variant="outlined"
            size="small"
            label={label}
            value={value}
            disabled={disabled}
            onChange={(event) => {
                const selected = options.find(
                    // MUI returns the MenuItem value as is, so compare as strings to
                    // cover both text and number options
                    (option) =>
                        String(option.value) === String(event.target.value),
                );
                if (selected) onChange(selected.value);
            }}
        >
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
}

type FilterMeldingProps = {
    children: React.ReactNode;
};

/** Message in place of the table, e.g. when no filter is chosen yet. */
export const FilterMelding = ({ children }: FilterMeldingProps) => (
    <div className={classes.melding}>
        <Typography klpColor="mediumDark">{children}</Typography>
    </div>
);

type FilterTreffProps = {
    antall: number;
};

/**
 * Number of rows in the table, as its title. mui-datatables also puts the
 * title in the table's caption, so screen readers get it there.
 */
export const FilterTreff = ({ antall }: FilterTreffProps) => {
    const { t } = useTranslation();

    return (
        <Typography klpTypographyVariant="small" klpColor="mediumDark">
            {t('tables.antallTreff', { count: antall })}
        </Typography>
    );
};

type FilterStackProps = {
    children: React.ReactNode;
};

/**
 * Stacks a FilterSok above a FilterRow, with some extra space down to the
 * table below.
 */
export const FilterStack = ({ children }: FilterStackProps) => (
    <div className={classes.stack}>{children}</div>
);

type TomFeltKnappProps = {
    // Label of the field, so each clear button has its own name
    felt: string;
    onClick: () => void;
};

const TomFeltKnapp = ({ felt, onClick }: TomFeltKnappProps) => {
    const { t } = useTranslation();

    return (
        <IconButton
            className={classes.tomFelt}
            size="small"
            aria-label={`${t('filterPanel.tomFelt')}: ${felt}`}
            onClick={onClick}
        >
            <Close fontSize="small" />
        </IconButton>
    );
};

// Clear handler for the date field below. Passed through context so the text
// field component given to the date picker can be defined once, and isn't
// remounted (losing focus) on every render.
const TomDatoContext = React.createContext<(() => void) | undefined>(undefined);

const DatoTekstfelt = (props: TextFieldProps) => {
    const onClear = useContext(TomDatoContext);
    const felt = typeof props.label === 'string' ? props.label : '';
    if (!onClear) return <TextField {...props} />;

    return (
        <TextField
            {...props}
            InputProps={{
                ...props.InputProps,
                // Keep the date picker's own calendar button after the clear
                // button
                endAdornment: (
                    <>
                        <TomFeltKnapp felt={felt} onClick={onClear} />
                        {props.InputProps?.endAdornment}
                    </>
                ),
            }}
        />
    );
};

type FilterDatePickerProps = { label: string; disabled?: boolean } & (
    | {
          value: Date;
          onChange: (date: Date) => void;
          clearable?: false;
      }
    | {
          value: Date | null;
          // Called with null when the field is cleared
          onChange: (date: Date | null) => void;
          // Lets the user empty the field, so the date isn't filtered on
          clearable: true;
      }
);

/** Date field for a FilterRow, in the same format as the dates in the tables. */
export const FilterDatePicker = (props: FilterDatePickerProps) => {
    const { label, value, clearable, disabled } = props;
    const tom = () => {
        if (props.clearable) props.onChange(null);
    };

    return (
        <TomDatoContext.Provider
            value={clearable && value && !disabled ? tom : undefined}
        >
            <KeyboardDatePicker
                className={
                    clearable ? classes.datePickerClearable : classes.datePicker
                }
                fullWidth={false}
                allowKeyboardControl={true}
                label={label}
                disabled={disabled}
                variant="inline"
                inputVariant="outlined"
                size="small"
                InputAdornmentProps={{ position: 'end' }}
                TextFieldComponent={DatoTekstfelt}
                value={value}
                onChange={(date: Date | null) => {
                    if (date && isValid(date)) props.onChange(date);
                    else if (!date) tom();
                }}
                maxDate={new Date()}
                autoOk={true}
                format="dd.MM.yyyy"
            />
        </TomDatoContext.Provider>
    );
};

type FilterSokProps = {
    id: string;
    label: string;
    value?: string;
    onChange: (value: string) => void;
    // How long to wait after the last keystroke before searching
    debounceMs?: number;
    // Short text to the right of the field, e.g. how the search works with
    // the filters. Beside the field so nothing below moves when it shows.
    helperText?: string;
    // Called with the text in the field as it is typed, before the debounce,
    // e.g. to react to a search right away
    onInputChange?: (tekst: string) => void;
    disabled?: boolean;
    // Shown in the same row, before the field, e.g. a FilterToggle
    foran?: React.ReactNode;
};

/**
 * Search field for a list view. Searches when the user stops typing, or right
 * away on Enter.
 */
export const FilterSok = ({
    id,
    label,
    value,
    onChange,
    debounceMs = 1000,
    helperText,
    onInputChange,
    disabled = false,
    foran,
}: FilterSokProps) => {
    const [sok, setSok] = useState(value ?? '');
    const timer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timer.current), []);

    // Also covers changes from outside, like «Nullstill»
    useEffect(() => {
        onInputChange?.(sok);
    }, [sok, onInputChange]);

    // Follow outside changes, e.g. «Nullstill», but keep what the user is
    // typing, like a trailing space, when it is the same search
    useEffect(() => {
        setSok((forrige) =>
            forrige.trim() === (value ?? '') ? forrige : (value ?? ''),
        );
    }, [value]);

    const sokNaa = (tekst: string) => {
        clearTimeout(timer.current);
        const trimmed = tekst.trim();
        if (trimmed !== (value ?? '')) onChange(trimmed);
    };

    const handleChange = (tekst: string) => {
        setSok(tekst);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => sokNaa(tekst), debounceMs);
    };

    const tom = () => {
        setSok('');
        sokNaa('');
    };

    return (
        <div className={classes.filterSok}>
            {foran}
            <TextField
                id={id}
                className={classes.sokField}
                label={label}
                variant="outlined"
                size="small"
                value={sok}
                disabled={disabled}
                onChange={(event) => handleChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') sokNaa(sok);
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment:
                        sok && !disabled ? (
                            <InputAdornment position="end">
                                <TomFeltKnapp felt={label} onClick={tom} />
                            </InputAdornment>
                        ) : undefined,
                }}
            />
            {helperText && (
                <Typography klpTypographyVariant="small" klpColor="mediumDark">
                    {helperText}
                </Typography>
            )}
        </div>
    );
};

type FilterToggleProps = {
    label: string;
    // Shown after the label, e.g. how many there are
    antall?: number;
    aktiv: boolean;
    onChange: (aktiv: boolean) => void;
};

/**
 * On/off button for a quick choice in the filter panel, like «Tildelt meg».
 * Filled while on.
 */
export const FilterToggle = ({
    label,
    antall,
    aktiv,
    onChange,
}: FilterToggleProps) => (
    <Button
        className={classes.toggle}
        variant={aktiv ? 'contained' : 'outlined'}
        color="secondary"
        disableElevation={true}
        startIcon={<AssignmentInd />}
        aria-pressed={aktiv}
        onClick={() => onChange(!aktiv)}
    >
        {label}
        {antall !== undefined && (
            <span className={classes.antall}>{antall}</span>
        )}
    </Button>
);
